import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import type { Property } from 'csstype';
import { PositionFromProps } from '../../utils/styledcomponents';
import type { PositionProps } from '../../utils/styledcomponents';
import { getHighlightClassName } from '../../utils/animation';
import type { HighlightProps } from '../../utils/animation';
import { useClickCallback } from '../../utils/hooks';

export interface StateProps {
	soundEnabled: boolean;
	soundVolume: number;
}

export interface DispatchProps {}

interface InnerProps<T = string | number> extends HighlightProps {
	width?: Property.Width<T>;
	height?: Property.Height<T>;
	onClick?: () => void;
	primary?: boolean;
	disabled?: boolean;
}

export interface OwnProps extends InnerProps {}

export type Props = StateProps & DispatchProps & OwnProps;

const InnerButton = styled.button.attrs<InnerProps>((props) => ({
	disabled: props.disabled || !props.onClick,
	className: getHighlightClassName(props)
}))<InnerProps>`
	background-color: ${(props) =>
		props.primary ? props.theme.colors.red : props.theme.colors.white};
	border: ${(props) => props.theme.borderWidth.button} solid
		${(props) =>
			props.primary ? props.theme.colors.red : props.theme.colors.violet};
	border-radius: 999px;
	box-shadow: ${(props) => props.theme.shadow.button};
	color: ${(props) =>
		props.primary ? props.theme.colors.white : props.theme.colors.darkBlue};
	cursor: ${(props) =>
		props.onClick && !props.disabled ? 'pointer' : 'inherit'};
	font: ${(props) => props.theme.font.button};
	height: ${(props) => props.height};
	letter-spacing: 0.2vmin;
	overflow: hidden;
	padding: 1.2vmin;
	text-overflow: ellipsis;
	transition: box-shadow 0.25s, transform 0.2s ease-out;
	width: ${(props) => props.width};
	white-space: nowrap;
	&:focus {
		outline: none;
	}
	&:enabled:hover {
		transition: transform 0.2s ease-in;
		transform: scale(1.05);
	}
	&:enabled:active {
		transition: box-shadow 0.25s, transform 0.2s ease-in;
		box-shadow: none;
	}
	& > div {
		margin: 0 auto;
	}
`;

const Button: FunctionComponent<Props> = (props: PropsWithChildren<Props>) => {
	const { onClick, soundEnabled, soundVolume, ...otherProps } = props;
	const onClickThrottleWithSound = useClickCallback(
		onClick,
		soundEnabled,
		soundVolume
	);

	return <InnerButton onClick={onClickThrottleWithSound} {...otherProps} />;
};

export default styled(Button)<PositionProps>(PositionFromProps);
